/** @format */

import { useEffect, useRef } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { FaLock, FaSignOutAlt, FaStore, FaWhatsapp } from "react-icons/fa";
import { GrMail } from "react-icons/gr";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";
import { Link } from "react-router-dom";
import i18n from "../../utils/i18n";
import UserAvatar from "../../utils/UserAvatar";
import { email, phone, socialLinks, WishCardMessage } from "../../utils/utils";

// ─── Constants ────────────────────────────────────────────────────────────────

const NAV_LINKS = [
  { path: "/", label: "Home" },
  { path: "/shops", label: "Shop" },
  { path: "/about", label: "About" },
  { path: "/contact", label: "Contact" },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Thin divider */
const Divider = () => <hr className='border-slate-100 my-4' />;

/** Language picker pill */
const LangPicker = ({ changeLanguage }) => {
  const isFr = i18n.language === "fr";
  return (
    <div className='group relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-slate-50 hover:bg-white hover:border-slate-300 hover:shadow-sm transition-all duration-200 cursor-pointer select-none'>
      <img
        src={
          isFr ? "/images/frflag.png" : "/images/Flag_of_the_United_Kingdom.png"
        }
        alt='lang'
        className='w-5 h-auto rounded-sm'
      />
      <span className='text-xs font-semibold text-slate-600 uppercase'>
        {isFr ? "FR" : "EN"}
      </span>
      <MdOutlineKeyboardArrowDown className='text-slate-400 text-sm transition-transform duration-200 group-hover:rotate-180' />

      {/* Dropdown */}
      <div
        className='
          absolute top-[calc(100%+6px)] left-0
          invisible opacity-0 translate-y-1
          group-hover:visible group-hover:opacity-100 group-hover:translate-y-0
          transition-all duration-200
          bg-white border border-slate-200 rounded-xl shadow-lg shadow-slate-100
          py-1.5 w-36 z-50 overflow-hidden
        '
      >
        {[
          {
            code: "en",
            flag: "/images/Flag_of_the_United_Kingdom.png",
            label: "English",
          },
          { code: "fr", flag: "/images/frflag.png", label: "Français" },
        ].map(({ code, flag, label }) => (
          <button
            key={code}
            type='button'
            onClick={() => changeLanguage(code)}
            className={`
              w-full flex items-center gap-2.5 px-3 py-2 text-sm
              transition-colors duration-150
              ${
                i18n.language === code
                  ? "bg-orange-50 text-orange-600 font-semibold"
                  : "text-slate-700 hover:bg-slate-50"
              }
            `}
          >
            <img src={flag} className='w-5 rounded-sm' alt={code} />
            {label}
          </button>
        ))}
      </div>
    </div>
  );
};

/** Single nav link with active indicator */
const NavLink = ({ path, label, pathname, t, onClick }) => {
  const isActive = pathname === path;
  return (
    <li>
      <Link
        to={path}
        onClick={onClick}
        className={`
          relative flex items-center gap-3 px-3 py-2.5 rounded-xl
          text-sm font-semibold transition-all duration-150
          ${
            isActive
              ? "bg-orange-50 text-orange-500"
              : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
          }
        `}
      >
        {isActive && (
          <span className='absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-orange-500' />
        )}
        <span className='uppercase tracking-wide'>{t(label)}</span>
      </Link>
    </li>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const HeaderSidebar = ({
  showSliderbar,
  setShowSliderbar,
  t,
  pathname,
  customerInfo,
  handleLogout,
  handleSwitchToSeller,
  changeLanguage,
  redirect_to_wishlist_page,
  redirect_to_card_page,
  redirect_to_chat_page,
  wishList_count,
  card_total,
  totalCustommerUnread,
}) => {
  const sidebarRef = useRef(null);

  // Close on outside click (keyboard a11y)
  useEffect(() => {
    const handler = (e) => {
      if (
        !showSliderbar &&
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target)
      ) {
        setShowSliderbar(true);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showSliderbar, setShowSliderbar]);

  // Lock body scroll when open
  useEffect(() => {
    document.body.style.overflow = showSliderbar ? "" : "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [showSliderbar]);

  const close = () => setShowSliderbar(true);

  return (
    <div className='md-lg:block hidden'>
      {/* ── Backdrop ── */}
      <div
        onClick={close}
        aria-hidden='true'
        className={`
          fixed inset-0 z-30 bg-black/40 backdrop-blur-[2px]
          transition-all duration-300
          ${showSliderbar ? "opacity-0 pointer-events-none" : "opacity-100"}
        `}
      />

      {/* ── Drawer ── */}
      <aside
        ref={sidebarRef}
        className={`
          sm:w-[310px] w-[78%]
          fixed top-0 left-0 h-screen z-[9999]
          bg-white flex flex-col
          shadow-2xl shadow-slate-200
          transition-transform duration-300 ease-in-out
          ${showSliderbar ? "-translate-x-full" : "translate-x-0"}
          overflow-hidden
        `}
      >
        {/* ── Scrollable content ── */}
        <div className='flex-1 overflow-y-auto overscroll-contain'>
          {/* ── Header row ── */}
          <div className='flex items-center justify-between px-5 py-4 border-b border-slate-100'>
            <Link
              to='/'
              onClick={close}
              className='group flex items-center gap-2.5 py-1.5 px-3 rounded-md
                         bg-orange-500 hover:bg-orange-600
                         transition-all duration-200 ease-out
                         shadow-[0_2px_10px_rgba(249,115,22,0.35)]
                         hover:shadow-[0_4px_16px_rgba(249,115,22,0.45)]
                         hover:-translate-y-[1px]'
            >
              <img
                className='w-[36px] h-[36px] object-contain drop-shadow-sm'
                src='/images/logo.png'
                alt='Logo'
              />
              <span
                className='text-white text-lg font-extrabold tracking-widest
                              sm:block leading-none'
              >
                GOBYMALL
              </span>
            </Link>
            <button
              type='button'
              onClick={close}
              aria-label='Close menu'
              className='
                w-8 h-8 flex items-center justify-center rounded-full
                text-slate-500 hover:text-slate-900
                bg-slate-100 hover:bg-slate-200
                transition-colors duration-150
              '
            >
              <AiOutlineClose className='text-base' />
            </button>
          </div>

          <div className='px-5 py-4 space-y-0'>
            {/* ── Language + Auth ── */}
            <div className='flex items-center justify-between mb-4'>
              <LangPicker changeLanguage={changeLanguage} />

              {customerInfo ? (
                <div className='flex items-center gap-3'>
                  <Link to='/dashboard' onClick={close}>
                    <UserAvatar customerInfo={customerInfo} />
                  </Link>
                  <button
                    type='button'
                    onClick={() => {
                      handleLogout();
                      close();
                    }}
                    className='
                      flex items-center gap-1.5 px-3 py-1.5 rounded-full
                      text-xs font-semibold text-red-500
                      border border-red-100 bg-red-50
                      hover:bg-red-100 hover:border-red-200
                      transition-all duration-150
                    '
                  >
                    <FaSignOutAlt className='text-[11px]' />
                    {t("Logout")}
                  </button>
                </div>
              ) : (
                <Link
                  to='/login'
                  onClick={close}
                  className='
                    flex items-center gap-1.5 px-3 py-1.5 rounded-full
                    text-xs font-semibold text-slate-600
                    border border-slate-200 bg-slate-50
                    hover:bg-slate-100 hover:border-slate-300
                    transition-all duration-150
                  '
                >
                  <FaLock className='text-[11px]' />
                  {t("Login")}
                </Link>
              )}
            </div>

            <Divider />

            {/* ── WishList / Cart / Chat ── */}
            <div className='mb-4'>
              <WishCardMessage
                redirect_to_wishlist_page={redirect_to_wishlist_page}
                card_total={card_total}
                redirect_to_card_page={redirect_to_card_page}
                wishList_count={wishList_count}
                redirect_to_chat_page={redirect_to_chat_page}
                totalCustommerUnread={totalCustommerUnread}
              />
            </div>

            <Divider />

            {/* ── Navigation ── */}
            <nav>
              <p className='text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 px-1'>
                {t("Navigation")}
              </p>
              <ul className='flex flex-col gap-0.5'>
                {NAV_LINKS.map(({ path, label }) => (
                  <NavLink
                    key={path}
                    path={path}
                    label={label}
                    pathname={pathname}
                    t={t}
                    onClick={close}
                  />
                ))}
              </ul>
            </nav>

            <Divider />

            {/* ── Sell CTA ── */}
            <button
              type='button'
              onClick={() => {
                handleSwitchToSeller();
                close();
              }}
              className='
                w-full flex items-center justify-center gap-2
                py-2.5 px-4 rounded-xl
                bg-gradient-to-r from-orange-500 to-orange-400
                text-white text-sm font-bold
                shadow-md shadow-orange-200
                hover:from-orange-600 hover:to-orange-500
                hover:shadow-orange-300
                active:scale-[0.98]
                transition-all duration-200
              '
            >
              <FaStore className='text-sm' />
              {t("Sell on GOBYMALL")}
            </button>

            <Divider />

            {/* ── Social links ── */}
            <div>
              <p className='text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 px-1'>
                {t("Follow us")}
              </p>
              <div className='flex items-center gap-2 flex-wrap'>
                {socialLinks.map(({ icon, label, href }, i) => (
                  <a
                    key={i}
                    href={href}
                    aria-label={label}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='
                      w-9 h-9 flex items-center justify-center rounded-full
                      bg-slate-100 text-slate-600
                      hover:bg-orange-50 hover:text-orange-500
                      border border-transparent hover:border-orange-100
                      transition-all duration-150
                      text-base
                    '
                  >
                    {icon}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Footer (sticky bottom) ── */}
        <div className='border-t border-slate-100 px-5 py-4 bg-slate-50/80 space-y-3 flex-shrink-0'>
          {/* WhatsApp */}
          <a
            href={`https://wa.me/${phone}`}
            target='_blank'
            rel='noopener noreferrer'
            className='
              flex items-center gap-3 p-3 rounded-xl
              bg-green-50 border border-green-100
              hover:bg-green-100 hover:border-green-200
              transition-all duration-150 group
            '
          >
            <div
              className='
              w-9 h-9 rounded-full bg-green-500
              flex items-center justify-center flex-shrink-0
              shadow-sm shadow-green-200
              group-hover:scale-105 transition-transform duration-150
            '
            >
              <FaWhatsapp className='text-white text-base' />
            </div>
            <div className='flex flex-col min-w-0'>
              <span className='text-sm font-semibold text-green-700 truncate'>
                +{phone}
              </span>
              <span className='text-[11px] text-green-500'>
                {t("Support 33/45 time")}
              </span>
            </div>
          </a>

          {/* Email */}
          <div className='flex items-center gap-2.5 px-1'>
            <div className='w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0'>
              <GrMail className='text-slate-500 text-xs' />
            </div>
            <span className='text-xs text-slate-500 truncate'>{email}</span>
          </div>

          {/* App badge */}
          <div className='text-center'>
            <span
              className='
              inline-block text-[10px] font-bold tracking-widest uppercase
              text-orange-500 bg-orange-50 border border-orange-100
              px-3 py-1 rounded-full
            '
            >
              MultiVendor Platform
            </span>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default HeaderSidebar;
